/**
 * Event Reminders
 *
 * "Remind me before crew call for events I'm crewed for" — Google-Calendar
 * style rows of [method] × [timing], up to 2, applied globally to every event
 * the user holds an active assignment on (deliberately no per-event config).
 *
 * Saves only the `reminders` key; the server merges notificationPreferences,
 * so this card and the channel-toggle grid never clobber each other. The
 * Text method needs the verified SMS opt-in (the reminder is never consent).
 */

import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import {
  updateProfile,
  type EventReminder,
  type ReminderMethod,
  type ReminderTiming,
} from '../../api/auth';
import { getSmsConsentStatus } from '../../api/smsConsent';
import { getErrorMessage } from '../../api/client';

const MAX_REMINDERS = 2;

// Labels deliberately omit "before" — the row already ends with a static
// "before crew call", so an option like "1 day before" would read
// "1 day before before crew call".
const TIMING_OPTIONS: { value: ReminderTiming; label: string }[] = [
  { value: 'WEEK_BEFORE', label: '1 week' },
  { value: 'DAY_BEFORE', label: '1 day' },
  { value: 'NIGHT_BEFORE', label: 'The evening (8 PM)' },
  { value: 'TWO_HOURS_BEFORE', label: '2 hours' },
];

const METHOD_OPTIONS: { value: ReminderMethod; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'push', label: 'Push' },
  { value: 'sms', label: 'Text' },
];

function sanitize(raw: unknown): EventReminder[] {
  if (!Array.isArray(raw)) return [];
  return (raw as EventReminder[])
    .filter(
      (r) =>
        r &&
        TIMING_OPTIONS.some((t) => t.value === r.timing) &&
        METHOD_OPTIONS.some((m) => m.value === r.method)
    )
    .slice(0, MAX_REMINDERS);
}

export default function EventReminderSettings() {
  const { user, refreshUser } = useAuth();
  const { currentOrg, smsEnabled: orgSmsEnabled } = useOrganization();
  const [reminders, setReminders] = useState<EventReminder[]>(() =>
    sanitize(user?.notificationPreferences?.reminders)
  );
  const [saving, setSaving] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSmsConsentStatus()
      .then((s) => {
        if (!cancelled) setSmsEnabled(s.enabled);
      })
      .catch(() => {
        if (!cancelled) setSmsEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (next: EventReminder[]) => {
    const previous = reminders;
    setReminders(next);
    setSaving(true);
    try {
      await updateProfile({ notificationPreferences: { reminders: next } });
      await refreshUser();
      toast.success('Reminders saved', { id: 'event-reminders' });
    } catch (err) {
      setReminders(previous);
      toast.error(getErrorMessage(err), { id: 'event-reminders' });
    } finally {
      setSaving(false);
    }
  };

  const updateRow = (index: number, patch: Partial<EventReminder>) => {
    void save(reminders.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addReminder = () => {
    if (reminders.length >= MAX_REMINDERS) return;
    // Default to the pairing people most often want: a push nudge 2h out,
    // then an email the day before as the second reminder.
    const next: EventReminder =
      reminders.length === 0
        ? { method: 'push', timing: 'TWO_HOURS_BEFORE' }
        : { method: 'email', timing: 'DAY_BEFORE' };
    void save([...reminders, next]);
  };

  const removeReminder = (index: number) => {
    void save(reminders.filter((_, i) => i !== index));
  };

  if (!user) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Event Reminders</h2>
      <p className="text-sm text-gray-500 mb-4">
        Remind me before crew call for events I'm crewed for. These apply to every
        event you're assigned to work.
      </p>

      {reminders.length === 0 && (
        <p className="text-sm text-gray-500 mb-3">No reminders set.</p>
      )}

      <div className="space-y-2">
        {reminders.map((reminder, index) => (
          <div key={index} className="flex flex-wrap items-center gap-2">
            <select
              aria-label={`Reminder ${index + 1} method`}
              value={reminder.method}
              disabled={saving}
              onChange={(e) => updateRow(index, { method: e.target.value as ReminderMethod })}
              className="pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-rack focus:border-rack"
            >
              {METHOD_OPTIONS.map((m) => (
                <option
                  key={m.value}
                  value={m.value}
                  disabled={m.value === 'sms' && smsEnabled !== true}
                >
                  {m.label}
                  {m.value === 'sms' && smsEnabled !== true ? ' (requires opt-in)' : ''}
                </option>
              ))}
            </select>

            <select
              aria-label={`Reminder ${index + 1} timing`}
              value={reminder.timing}
              disabled={saving}
              onChange={(e) => updateRow(index, { timing: e.target.value as ReminderTiming })}
              className="pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-rack focus:border-rack"
            >
              {TIMING_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>

            <span className="text-sm text-gray-500">before crew call</span>

            <button
              type="button"
              aria-label={`Remove reminder ${index + 1}`}
              disabled={saving}
              onClick={() => removeReminder(index)}
              className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-gray-50"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>

      {reminders.length < MAX_REMINDERS && (
        <button
          type="button"
          disabled={saving}
          onClick={addReminder}
          className="mt-3 text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
        >
          + Add reminder
        </button>
      )}

      <p className="mt-3 text-xs text-gray-500">
        Reminders use each event's crew call time (or its start time when no crew
        call is set). Text reminders also require the verified opt-in below.
        {!orgSmsEnabled &&
          ` ${currentOrg?.orgName || 'Your organization'} doesn't currently send text notifications.`}
      </p>
    </div>
  );
}
